import { useMemo, useState } from "react";
import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";

// The kit's own, so the trigger takes focus. MUI's Tooltip opens on
// hover and on focus, and a Chip, a Box or a Typography takes neither.
import Tooltip from "./Tooltip.jsx";

/**
 * One record and everything around it (LO-76).
 *
 * ## Why a ring and not a force-directed hairball
 *
 * The kernel stops traversal at one hop on purpose — arbitrary depth is a
 * query language and a way to walk a whole tenant in one request. So the
 * shape the API actually has *is* a hub and spokes, and drawing that is not a
 * simplification of a graph view, it is the graph view. There are also 2,075
 * edges over 2,308 objects in this org, so the whole-graph version would be
 * an unreadable ball whether or not an endpoint existed to feed it.
 *
 * ## Three things this deliberately does not do
 *
 * **It does not hide a relationship the caller cannot follow.** The manifest
 * is per type, so an app that reads Organizations and never declared Person
 * cannot hop to people. That spoke is drawn refused rather than omitted: a
 * diagram missing a piece with no sign a piece is missing is worse than no
 * diagram, because it is believed.
 *
 * **It does not claim the edge count is what you can see.** Those differ the
 * moment a neighbour is tagged beyond the caller, and the ring shows a
 * sample. The count says edges and the caption says so.
 *
 * **It is not the only representation.** An SVG is not keyboard-navigable and
 * colour is not a signal a colour-blind operator can read, so everything in
 * the picture is also in the list underneath, in words.
 */

const CX = 0;
const CY = 0;
const HUB_R = 130;
const NODE_R = 212;
const PAD = 26;

function polar(angle, radius) {
  return [CX + radius * Math.cos(angle), CY + radius * Math.sin(angle)];
}

/** Roughly how wide this text renders at this size.
 *
 * SVG has no layout pass, so a fixed-width pill truncated "organizations 1"
 * to "organizatio… 1" — a label that has lost the only part carrying meaning.
 * 0.58em per character is close enough for a system sans at these sizes, and
 * being a little generous costs nothing but whitespace.
 */
function textWidth(text, size) {
  return String(text ?? "").length * size * 0.58;
}

/** Truncated for a label slot, with the full value kept for the title. */
function short(text, max = 18) {
  const value = String(text ?? "");
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

export default function RecordGraph({ graph }) {
  const theme = useTheme();
  const [focused, setFocused] = useState(null);

  const relationships = graph?.relationships || [];

  const { layout, viewBox } = useMemo(() => {
    // Spokes evenly around the centre, starting at the top. Where the record
    // came from is not drawn as a node here — it is one line of prose above
    // the provenance table ("xero synced this via sync.person.upsert, with
    // nobody there"), which says more than a labelled circle would.
    const step = (2 * Math.PI) / Math.max(relationships.length, 1);
    const placed = relationships.map((rel, i) => {
      const angle = -Math.PI / 2 + i * step;
      const [hx, hy] = polar(angle, HUB_R);
      const hubLabel = rel.permitted
        ? `${short(rel.name, 16)}  ${rel.edges}`
        : `${short(rel.name, 14)}  refused`;
      const hubW = Math.max(96, textWidth(hubLabel, 12) + 26);
      const nodes = (rel.nodes || []).map((node, j, all) => {
        // Fan the sampled neighbours around their hub's angle rather than
        // stacking them, so a spoke with six reads as six.
        const spread = Math.min(0.34 * all.length, 1.1);
        const offset = all.length === 1
          ? 0
          : -spread / 2 + (j * spread) / (all.length - 1);
        const [nx, ny] = polar(angle + offset, NODE_R);
        return { ...node, x: nx, y: ny };
      });
      return { ...rel, angle, hx, hy, nodes, hubLabel, hubW };
    });

    // Fit the box to what is actually drawn. A record with one relationship
    // was rendered into a canvas sized for four, so two thirds of the panel
    // was blank and the drawing sat in a corner of it.
    let minX = -70, maxX = 70, minY = -60, maxY = 60;
    for (const rel of placed) {
      minX = Math.min(minX, rel.hx - rel.hubW / 2);
      maxX = Math.max(maxX, rel.hx + rel.hubW / 2);
      minY = Math.min(minY, rel.hy - 16);
      maxY = Math.max(maxY, rel.hy + 16);
      for (const node of rel.nodes) {
        const half = textWidth(short(node.label, 16), 11) / 2;
        minX = Math.min(minX, node.x - half);
        maxX = Math.max(maxX, node.x + half);
        minY = Math.min(minY, node.y - 22);
        maxY = Math.max(maxY, node.y + 10);
      }
    }
    return {
      layout: placed,
      viewBox: [
        minX - PAD, minY - PAD,
        maxX - minX + PAD * 2, maxY - minY + PAD * 2,
      ].join(" "),
    };
  }, [relationships]);

  // `theme.vars`, not `theme.palette` — found live, in dark mode: this kit's
  // theme is CSS-variables-backed (`extendTheme`, `colorSchemeSelector:
  // "data"`), so `theme.palette.*` is the baked-in default scheme's static
  // value and does not follow which scheme is actually active. `sx` resolves
  // this correctly on its own; a raw `fill` attribute, set from JS instead
  // of `sx`, has to ask for the CSS variable explicitly. Every label in this
  // diagram was near-black text on a near-black drawer background in dark
  // mode — invisible, not merely low-contrast — until this was fixed.
  const vars = theme.vars?.palette || theme.palette;
  const palette = {
    centre: vars.kernel?.permitted || vars.primary.main,
    hub: vars.text.secondary,
    refused: vars.kernel?.attention || vars.warning.main,
    line: vars.divider,
    node: vars.text.primary,
    paper: vars.background.paper,
  };

  const record = graph?.record;
  const centreLabel = record?.fields?.name
    || record?.fields?.display_name
    || record?.fields?.invoice_number
    || record?.type;

  return (
    <Box>
      {/* Its own scroll container: the page body never scrolls sideways. */}
      <Box sx={{ overflowX: "auto", pb: 1 }}>
        <Box
          component="svg"
          viewBox={viewBox}
          role="img"
          aria-label={
            `${centreLabel} and ${relationships.length} relationships. ` +
            `The same information is listed below this diagram.`
          }
          sx={{
            width: "100%",
            minWidth: 520,
            maxHeight: 380,
            height: "auto",
            display: "block",
          }}
        >
          {layout.map((rel) => {
            const dim = focused && focused !== rel.name;
            const colour = rel.permitted ? palette.line : palette.refused;
            return (
              <g key={rel.name} opacity={dim ? 0.25 : 1}>
                <line
                  x1={CX}
                  y1={CY}
                  x2={rel.hx}
                  y2={rel.hy}
                  stroke={colour}
                  strokeWidth={1.5}
                  // Dashed carries the refusal as shape, so it is not colour
                  // alone doing the work.
                  strokeDasharray={rel.permitted ? undefined : "4 3"}
                />
                {rel.nodes.map((node) => (
                  <line
                    key={node.id}
                    x1={rel.hx}
                    y1={rel.hy}
                    x2={node.x}
                    y2={node.y}
                    stroke={palette.line}
                    strokeWidth={1}
                  />
                ))}
              </g>
            );
          })}

          {layout.map((rel) => {
            const dim = focused && focused !== rel.name;
            return (
              <g key={rel.name} opacity={dim ? 0.25 : 1}>
                {rel.nodes.map((node) => (
                  <g key={node.id}>
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={5}
                      fill={palette.paper}
                      stroke={palette.node}
                      strokeWidth={1.25}
                    />
                    <text
                      x={node.x}
                      y={node.y - 11}
                      textAnchor="middle"
                      fontSize={11}
                      fill={palette.node}
                      // `named: false` means the kernel had no label field
                      // for this type, so the text is a fallback rather than
                      // the record's name. Italic says that without a word.
                      fontStyle={node.named ? "normal" : "italic"}
                    >
                      {short(node.label, 16)}
                    </text>
                  </g>
                ))}

                <rect
                  x={rel.hx - rel.hubW / 2}
                  y={rel.hy - 13}
                  width={rel.hubW}
                  height={26}
                  rx={13}
                  fill={palette.paper}
                  stroke={rel.permitted ? palette.hub : palette.refused}
                  strokeDasharray={rel.permitted ? undefined : "4 3"}
                />
                <text
                  x={rel.hx}
                  y={rel.hy + 4}
                  textAnchor="middle"
                  fontSize={12}
                  fill={rel.permitted ? palette.node : palette.refused}
                >
                  {rel.hubLabel}
                </text>
              </g>
            );
          })}

          <circle
            cx={CX}
            cy={CY}
            r={46}
            fill={palette.paper}
            stroke={palette.centre}
            strokeWidth={2}
          />
          <text
            x={CX}
            y={CY - 2}
            textAnchor="middle"
            fontSize={12}
            fontWeight={600}
            fill={palette.node}
          >
            {short(centreLabel, 15)}
          </text>
          <text
            x={CX}
            y={CY + 14}
            textAnchor="middle"
            fontSize={10}
            fill={palette.hub}
          >
            {record?.type}
          </text>
        </Box>
      </Box>

      {/* The same thing in words. The diagram is a convenience, not the
          only way to read this. */}
      <Stack spacing={1} sx={{ mt: 1 }}>
        {layout.map((rel) => (
          <Box
            key={rel.name}
            tabIndex={0}
            onMouseEnter={() => setFocused(rel.name)}
            onMouseLeave={() => setFocused(null)}
            onFocus={() => setFocused(rel.name)}
            onBlur={() => setFocused(null)}
            sx={{
              borderRadius: 1,
              px: 1,
              py: 0.5,
              outlineOffset: 2,
              "&:focus-visible": {
                outline: (t) => `2px solid ${t.palette.primary.main}`,
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "baseline", flexWrap: "wrap" }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {rel.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                → {rel.target}
              </Typography>
              {rel.permitted ? (
                <Tooltip title="Edges, not records you can see. Those differ when a neighbour is tagged beyond your permissions.">
                  <Typography variant="caption" color="text.secondary">
                    {rel.edges} {rel.edges === 1 ? "edge" : "edges"}
                    {rel.sampled ? `, showing ${rel.nodes.length}` : ""}
                  </Typography>
                </Tooltip>
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  label="refused"
                  sx={{
                    color: "kernel.attention",
                    borderColor: "kernel.attention",
                  }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {rel.permitted ? rel.description : rel.detail}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
